import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../pages/api';
import { useNavigate } from 'react-router-dom';

const Form_18 = () => {
    const navigate = useNavigate();

    // Default section structure
    const initialSection = { line: '', machine: '', from: '', to: '', issue: '' };
    const defaultFormData = { date: '', sections: Array(5).fill({ ...initialSection }) };

    // Initialize form data with fallback
    const [formData, setFormData] = useState(() => {
        const savedData = sessionStorage.getItem('MachineBreakdownF18formData');
        try {
            const parsedData = savedData ? JSON.parse(savedData) : defaultFormData;
            return {
                date: parsedData.date || '',
                sections: Array.isArray(parsedData.sections) && parsedData.sections.length === 5
                    ? parsedData.sections.map(section => ({ ...initialSection, ...section }))
                    : Array(5).fill({ ...initialSection }),
            };
        } catch (error) {
            console.error('Error parsing saved data:', error);
            return defaultFormData;
        }
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Prefill date and fetch inspection data
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        if (inspectionDate) setFormData((prev) => ({ ...prev, date: inspectionDate }));

        const inspectionData = sessionStorage.getItem('inspectionData');
        if (inspectionData && !formData.sections.some((s) => Object.values(s).some(Boolean))) {
            try {
                const parsed = JSON.parse(inspectionData).MachineBreakdownHistory || {};
                setFormData((prev) => ({
                    ...prev,
                    date: parsed.date || prev.date,
                    sections: prev.sections.map((section, i) => ({
                        ...section,
                        ...(parsed.sections?.[i] || {}),
                    })),
                }));
            } catch (error) {
                console.error('Error parsing inspectionData:', error);
            }
        }
    }, []);

    useEffect(() => sessionStorage.setItem('MachineBreakdownF18formData', JSON.stringify(formData)), [formData]);

    const handleChange = (e, index) => {
        const { name, value } = e.target;
        if (name === 'date') {
            setFormData((prev) => ({ ...prev, [name]: value }));
        } else {
            setFormData((prev) => ({
                ...prev,
                sections: prev.sections.map((s, i) => (i === index ? { ...s, [name]: value } : s)),
            }));
        }
        setErrors((prev) => ({ ...prev, [`${name}${index}`]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Remove validation since nothing is required
        setIsLoading(true);
        try {
            const payload = {
                projectId: localStorage.getItem('selectedProject'),
                inspectionSupplier: sessionStorage.getItem('inspectionSupplier'),
                inspectionDate: sessionStorage.getItem('inspectionDate'),
                inspectionShift: sessionStorage.getItem('inspectionShift'),
                inspectorName: sessionStorage.getItem('username'),
                status: 'completed',
                MachineBreakdownHistory: formData,
            };
            await submitFormData(payload);
            alert('Form submitted successfully!');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(`Failed to submit form: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-4 rounded-lg shadow-lg space-y-4">
            <h1 className="text-lg font-semibold mb-2">Machine Breakdown History</h1>

            {/* Date Field */}
            <div>
                <label className="block text-sm font-semibold mb-1" htmlFor="date">Date</label>
                <input
                    id="date"
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    readOnly
                    className={`w-full p-1 border rounded ${errors.date ? 'border-red-500' : 'focus:ring-blue-500'}`}
                />
                {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
            </div>

            {/* 5 Repeating Sections */}
            {formData.sections.map((section, index) => (
                <div key={index} className="space-y-2 border-2 border-gray-300 p-4 rounded-lg shadow-sm">
                    <div className="grid grid-cols-2 gap-2">
                        {['line', 'machine'].map((name) => (
                            <div key={name}>
                                <label className="block text-sm font-semibold mb-1" htmlFor={`${name}${index}`}>
                                    {name.charAt(0).toUpperCase() + name.slice(1)}
                                </label>
                                <input
                                    id={`${name}${index}`}
                                    name={name}
                                    value={section[name]}
                                    onChange={(e) => handleChange(e, index)}
                                    className={`w-full p-1 border rounded ${errors[`${name}${index}`] ? 'border-red-500' : 'focus:ring-blue-500'}`}
                                />
                                {errors[`${name}${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`${name}${index}`]}</p>}
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {['from', 'to', 'issue'].map((name) => (
                            <div key={name}>
                                <label className="block text-sm font-semibold mb-1" htmlFor={`${name}${index}`}>
                                    {name.charAt(0).toUpperCase() + name.slice(1)}
                                </label>
                                {name === 'issue' ? (
                                    <textarea
                                        id={`${name}${index}`}
                                        type= "time"
                                        name={name}
                                        value={section[name]}
                                        onChange={(e) => handleChange(e, index)}
                                        className={`w-full p-1 border rounded ${errors[`${name}${index}`] ? 'border-red-500' : 'focus:ring-blue-500'}`}
                                    />
                                ) : (
                                    <input
                                        id={`${name}${index}`}
                                        name={name}
                                        type="time"
                                        value={section[name]}
                                        onChange={(e) => handleChange(e, index)}
                                        className={`w-full p-1 border rounded ${errors[`${name}${index}`] ? 'border-red-500' : 'focus:ring-blue-500'}`}
                                    />
                                )}
                                {errors[`${name}${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`${name}${index}`]}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-4">
                <button type="button" onClick={() => navigate('/inspections/section2/form17')} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
                    Previous
                </button>
                <button type="submit" className="px-4 py-2 bg-[#424752] text-white rounded hover:bg-gray-500" disabled={isLoading}>
                    {isLoading ? 'Submitting...' : 'Submit'}
                </button>
                <button type="button" onClick={() => navigate('/inspections/section3/form19')} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
                    Next
                </button>
            </div>
        </form>
    );
};

export default Form_18;